import { useMemo } from "react";
import { SPRINTS, BUSINESS_AREAS, INITIATIVE_STAGES, DRIVERS, formatDate } from "./constants";

// ─── Small stat card ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, icon }) {
  return (
    <div style={{
      background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10,
      padding:"14px 18px", flex:"1 1 140px", minWidth:0,
      borderTop:`3px solid ${accent || "var(--accent)"}`,
    }}>
      <div style={{ fontSize:22, marginBottom:4 }}>{icon}</div>
      <div style={{ fontSize:26, fontWeight:900, color:"var(--text-primary)", lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)", marginTop:3 }}>{label}</div>
      {sub && <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:2 }}>{sub}</div>}
    </div>
  );
}

// ─── Horizontal bar ──────────────────────────────────────────────────────────
function HBar({ label, count, total, color, bg }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:5 }}>
      <div style={{ width:130, flexShrink:0, fontSize:11, fontWeight:600, color:"var(--text-secondary)",
        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={label}>{label}</div>
      <div style={{ flex:1, height:8, borderRadius:4, background:"var(--bg-subtle)", overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", borderRadius:4,
          background: color || "var(--accent)", transition:"width 0.4s" }} />
      </div>
      <div style={{ width:36, flexShrink:0, textAlign:"right", fontSize:11, fontWeight:700, color:"var(--text-secondary)" }}>{count}</div>
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────────────
function Section({ title, children, style }) {
  return (
    <div style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10,
      padding:"16px 20px", ...style }}>
      <div style={{ fontSize:11, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase",
        letterSpacing:"0.08em", marginBottom:14 }}>{title}</div>
      {children}
    </div>
  );
}

// ─── DashboardView ────────────────────────────────────────────────────────────
export default function DashboardView({ projects, backlog, completed, lanes = [], scoringMethod = "score" }) {
  const allItems = useMemo(() => [
    ...projects.map((p)  => ({ ...p, _bucket:"scheduled" })),
    ...backlog.map((p)   => ({ ...p, _bucket:"backlog"   })),
    ...completed.map((p) => ({ ...p, _bucket:"completed" })),
  ], [projects, backlog, completed]);

  const today      = useMemo(() => new Date(), []);
  const inProgress = projects.filter((p) => p.status === "inprogress").length;
  const atRisk     = allItems.filter((p) => p.atRisk || p.legalRisk || p.appStoreRisk || p.externalDependency?.trim()).length;
  const aiCount    = allItems.filter((p) => p.aiInitiative).length;
  const avgScore   = (() => {
    const scored = allItems.filter((p) => p.score > 0);
    if (!scored.length) return "—";
    return (scored.reduce((s, p) => s + p.score, 0) / scored.length).toFixed(1);
  })();

  // Business area breakdown
  const byBizArea = useMemo(() => {
    const counts = {};
    allItems.forEach((p) => {
      if (p.businessArea && BUSINESS_AREAS[p.businessArea]) {
        counts[p.businessArea] = (counts[p.businessArea] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([k, n]) => ({ key: k, label: BUSINESS_AREAS[k].label, count: n, color: BUSINESS_AREAS[k].color }))
      .sort((a, b) => b.count - a.count);
  }, [allItems]);

  // Stage breakdown
  const byStage = useMemo(() => {
    const counts = {};
    allItems.forEach((p) => { if (p.initiativeStage) counts[p.initiativeStage] = (counts[p.initiativeStage] || 0) + 1; });
    return Object.entries(INITIATIVE_STAGES).map(([k, s]) => ({ key: k, label: s.label, count: counts[k] || 0, color: s.color, icon: s.icon }));
  }, [allItems]);

  // Driver breakdown
  const byDriver = useMemo(() => {
    const counts = {};
    allItems.forEach((p) => { if (p.driver) counts[p.driver] = (counts[p.driver] || 0) + 1; });
    return Object.entries(DRIVERS).map(([k, d]) => ({ key: k, label: d.label, count: counts[k] || 0, color: d.color }));
  }, [allItems]);

  // Team load: projects per lane
  const byLane = useMemo(() => lanes.map((l) => ({
    lane: l,
    scheduled: projects.filter((p) => p.lane === l.id).length,
    backlog:    backlog.filter((p)   => p.lane === l.id).length,
    completed:  completed.filter((p) => p.lane === l.id).length,
  })), [projects, backlog, completed, lanes]);

  // Upcoming launches: projects w/ target date or sprint end within 30/60/90 days
  const upcoming = useMemo(() => {
    const results = [];
    const getDate = (p) => {
      if (p.targetLaunchDate) return new Date(p.targetLaunchDate + "T00:00:00");
      if (p.sprint !== undefined && SPRINTS[p.sprint]) {
        const s = SPRINTS[p.sprint];
        return new Date(s.start.getTime() + (p.sprints || 1) * 14 * 24 * 3600 * 1000);
      }
      return null;
    };
    [...projects, ...backlog].forEach((p) => {
      const d = getDate(p);
      if (!d || d < today) return;
      const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));
      if (diffDays <= 90) results.push({ ...p, _launchDate: d, _diffDays: diffDays });
    });
    return results.sort((a, b) => a._launchDate - b._launchDate);
  }, [projects, backlog, today]);

  // Risk breakdown
  const risks = [
    { label:"At Risk",          count: allItems.filter((p) => p.atRisk).length,                              color:"#EF4444", icon:"🚩" },
    { label:"Legal Risk",       count: allItems.filter((p) => p.legalRisk).length,                           color:"#7C3AED", icon:"⚖️" },
    { label:"App Store Risk",   count: allItems.filter((p) => p.appStoreRisk).length,                        color:"#0369A1", icon:"📱" },
    { label:"Ext. Blocker",     count: allItems.filter((p) => p.externalDependency?.trim()).length,           color:"#D97706", icon:"🔗" },
    { label:"Partner-Gated",    count: allItems.filter((p) => p.partnerDependencies?.trim()).length,          color:"#0891B2", icon:"🤝" },
    { label:"Has Dependencies", count: allItems.filter((p) => p.blockedBy?.length > 0).length,               color:"#92400E", icon:"⛓" },
  ].filter((r) => r.count > 0);

  const now30  = upcoming.filter((p) => p._diffDays <= 30);
  const now60  = upcoming.filter((p) => p._diffDays > 30 && p._diffDays <= 60);
  const now90  = upcoming.filter((p) => p._diffDays > 60);

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"20px 24px", display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Stat cards ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(150px, 1fr))", gap:12 }}>
        <StatCard label="Scheduled" value={projects.length} icon="📅" accent="var(--accent)"
          sub={`${inProgress} in progress`} />
        <StatCard label="Backlog" value={backlog.length} icon="📋" accent="var(--color-info,#8B5CF6)"
          sub="Unscheduled" />
        <StatCard label="Completed" value={completed.length} icon="✅" accent="var(--color-success,#22C55E)"
          sub="All time" />
        <StatCard label="Avg Score" value={avgScore} icon="⭐" accent="#F59E0B"
          sub={scoringMethod === "rice" ? "RICE" : "0–5 weighted"} />
        <StatCard label="At Risk" value={atRisk} icon="🚩" accent="var(--color-danger,#EF4444)"
          sub="Any risk flag" />
        <StatCard label="AI Initiatives" value={aiCount} icon="🤖" accent="var(--accent)"
          sub={`${allItems.length > 0 ? Math.round(aiCount/allItems.length*100) : 0}% of total`} />
      </div>

      {/* ── Middle row ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>

        {/* Business area */}
        <Section title="By Business Area">
          {byBizArea.length === 0
            ? <div style={{ fontSize:12, color:"var(--text-muted)", fontStyle:"italic" }}>No business areas set</div>
            : byBizArea.map((b) => (
                <HBar key={b.key} label={b.label} count={b.count} total={allItems.length} color={b.color} />
              ))}
        </Section>

        {/* Stage + Driver */}
        <Section title="Initiative Stage">
          {byStage.filter((s) => s.count > 0).map((s) => (
            <HBar key={s.key} label={`${s.icon} ${s.label}`} count={s.count} total={allItems.length} color={s.color} />
          ))}
          <div style={{ height:1, background:"var(--border)", margin:"12px 0 10px" }} />
          <div style={{ fontSize:11, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>By Driver</div>
          {byDriver.filter((d) => d.count > 0).map((d) => (
            <HBar key={d.key} label={d.label} count={d.count} total={allItems.length} color={d.color} />
          ))}
        </Section>

        {/* Risks + Team load */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <Section title="Risk Flags">
            {risks.length === 0
              ? <div style={{ fontSize:12, color:"var(--text-muted)", fontStyle:"italic" }}>No risk flags set — great! 🎉</div>
              : risks.map((r) => (
                  <div key={r.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <span style={{ fontSize:14 }}>{r.icon}</span>
                    <span style={{ flex:1, fontSize:11, fontWeight:600, color:"var(--text-secondary)" }}>{r.label}</span>
                    <span style={{ fontSize:13, fontWeight:900, color:r.color }}>{r.count}</span>
                  </div>
                ))}
          </Section>

          <Section title="Team Load">
            {byLane.map(({ lane, scheduled, backlog: bl, completed: done }) => (
              <div key={lane.id} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:lane.color, flexShrink:0 }} />
                <span style={{ flex:1, fontSize:11, fontWeight:700, color:"var(--text-secondary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lane.label}</span>
                <span title="Scheduled" style={{ fontSize:10, fontWeight:800, color:"var(--accent)", minWidth:20, textAlign:"center" }}>{scheduled}</span>
                <span title="Backlog"   style={{ fontSize:10, color:"var(--color-info,#8B5CF6)", minWidth:20, textAlign:"center" }}>{bl}</span>
                <span title="Done"      style={{ fontSize:10, color:"var(--color-success,#22C55E)", minWidth:20, textAlign:"center" }}>{done}</span>
              </div>
            ))}
            <div style={{ display:"flex", gap:8, marginTop:8, paddingTop:8, borderTop:"1px solid var(--border)" }}>
              {[["var(--accent)","Sched"],["var(--color-info,#8B5CF6)","Backlog"],["var(--color-success,#22C55E)","Done"]].map(([c, l]) => (
                <span key={l} style={{ fontSize:9, color:c, fontWeight:800, display:"flex", alignItems:"center", gap:3 }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:c, display:"inline-block" }} />{l}
                </span>
              ))}
            </div>
          </Section>
        </div>
      </div>

      {/* ── Upcoming launches ── */}
      {upcoming.length > 0 && (
        <Section title="Upcoming Launches (Next 90 Days)">
          {[
            { label:"Next 30 Days", items: now30, color:"#EF4444" },
            { label:"31 – 60 Days", items: now60, color:"#F59E0B" },
            { label:"61 – 90 Days", items: now90, color:"#22C55E" },
          ].filter((g) => g.items.length > 0).map((group) => (
            <div key={group.label} style={{ marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:800, color:group.color, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:7 }}>{group.label} ({group.items.length})</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {group.items.map((p) => {
                  const lane = lanes.find((l) => l.id === p.lane);
                  return (
                    <div key={p.id} style={{
                      background:"var(--bg-subtle)", border:"1px solid var(--border)", borderRadius:7,
                      borderLeft:`3px solid ${lane?.color || group.color}`,
                      padding:"7px 12px", minWidth:160, maxWidth:260,
                    }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"var(--text-primary)", marginBottom:3,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                      <div style={{ fontSize:10, color:"var(--text-muted)", display:"flex", gap:8 }}>
                        <span style={{ color: lane?.color, fontWeight:700 }}>{lane?.label}</span>
                        <span style={{ fontWeight:600, color:group.color }}>
                          {p.targetLaunchDate
                            ? new Date(p.targetLaunchDate + "T00:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric" })
                            : formatDate(p._launchDate)}
                        </span>
                        {p._diffDays <= 7 && <span style={{ color:"#EF4444", fontWeight:800 }}>⚠ {p._diffDays}d</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </Section>
      )}

      {upcoming.length === 0 && (
        <Section title="Upcoming Launches">
          <div style={{ textAlign:"center", padding:"24px 0", color:"var(--text-muted)", fontSize:12, fontStyle:"italic" }}>
            No launches scheduled in the next 90 days with target dates set.
          </div>
        </Section>
      )}

    </div>
  );
}
